import { useState, useEffect, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Select } from "../../components/Select/Select";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/Toast";
import { ApiError } from "../../lib/errors";
import { inviteMember } from "./api";
import styles from "./InviteMemberModal.module.css";

interface Props {
  projectId: string;
  projectName?: string;
  open: boolean;
  onClose: () => void;
}

// Simple, permissive email check — sufficient client-side; server is the
// authoritative validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Mode = "send" | "resend";

export function InviteMemberModal({
  projectId,
  projectName,
  open,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("send");

  // Reset form whenever the modal toggles open.
  useEffect(() => {
    if (open) {
      setEmail("");
      setEmailError(null);
      setServerError(null);
      setMode("send");
    }
  }, [open]);

  const mut = useMutation({
    mutationFn: (e: string) => inviteMember(projectId, e),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", projectId] });
      qc.invalidateQueries({ queryKey: ["pending-invites", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId, "activity"] });
      push({ variant: "success", message: `Invite sent to ${email.trim()}.` });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === "invite_already_exists") {
          setMode("resend");
          setServerError(
            `An invite was already sent to ${email.trim()}. Resend instead?`,
          );
          return;
        }
        if (err.code === "already_member") {
          setMode("send");
          setServerError(`${email.trim()} is already a member.`);
          return;
        }
      }
      setServerError("Something went wrong. Please try again.");
    },
  });

  function validate(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return "Email is required.";
    if (!EMAIL_RE.test(trimmed)) return "Enter a valid email address.";
    return null;
  }

  function submit() {
    const err = validate(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError(null);
    setServerError(null);
    mut.mutate(email.trim().toLowerCase());
  }

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  const title = projectName
    ? `Invite member to ${projectName}`
    : "Invite member";

  const ctaLabel = mut.isPending
    ? mode === "resend"
      ? "Resending…"
      : "Sending…"
    : mode === "resend"
      ? "Resend invite"
      : "Send invite";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            <Send size={14} aria-hidden="true" />
            <span style={{ marginLeft: 6 }}>{ctaLabel}</span>
          </Button>
        </>
      }
    >
      <form onSubmit={onFormSubmit} noValidate>
        <p className={styles.intro}>
          They&apos;ll receive an email with a one-time link to set up their
          account or accept the invite.
        </p>

        {serverError && (
          <div className={styles.field}>
            <Alert variant={mode === "resend" ? "warn" : "danger"}>
              {serverError}
            </Alert>
          </div>
        )}

        <div className={styles.field}>
          <Input
            label="Email address"
            type="email"
            autoFocus
            placeholder="colleague@studio.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            error={emailError ?? undefined}
            required
          />
        </div>

        <div className={styles.field}>
          <label
            htmlFor="invite-role"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 12,
              fontWeight: 500,
              marginBottom: 6,
              color: "var(--text-main)",
            }}
          >
            <span>Role</span>
            <span
              style={{
                fontSize: 11,
                color: "var(--text-faint)",
                fontWeight: 400,
              }}
            >
              Only project members for project invites
            </span>
          </label>
          <Select
            id="invite-role"
            value="project_user"
            onChange={() => {
              /* locked — single option */
            }}
            options={[{ value: "project_user", label: "Member" }]}
            disabled
          />
          <div className={styles.helper}>
            Member has full access to project config, team, and API keys. To
            grant Super admin, invite from{" "}
            <Link to="/users" onClick={onClose}>
              Platform users
            </Link>
            .
          </div>
        </div>

        <Alert variant="warn">
          Invite link expires in <strong>72 hours</strong>. You can resend or
          revoke from the team page.
        </Alert>

        {/* Hidden submit so Enter inside the email field submits the form. */}
        <button
          type="submit"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: "none" }}
        />
      </form>
    </Modal>
  );
}
